import { useState } from 'react';

type Error = {
    [key: string]: string | undefined;
};

type Option = {
    id?: string;
    name?: string;
    field?: string;
    url?: string;
};

const useForm = <T extends Record<string, unknown>, V extends string | { [key: string]: string } | File>(
    initInput: T,
    callback: () => void,
    validator: (field: string, inputValue: V, input?: T) => { [key: string]: string } | null
): {
    input: T;
    errors: { [key: string]: string | undefined };
    setInput: React.Dispatch<React.SetStateAction<T>>;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    resetForm: () => void;
    submit: (e: React.FormEvent<HTMLFormElement>) => void;
    handleChangeSelect: (option: { [key: string]: string }) => void;
} => {
    const [input, setInput] = useState(initInput);
    const [errors, setErrors] = useState<Error>({});

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, type, value } = e.target;

        clearValidation(name);

        if (type === 'file' && e.target instanceof HTMLInputElement) {
            const files = e.target.files;
            setInput((prevState) => ({
                ...prevState,
                [name]: files?.[0] || null,
            }));
        } else {
            setInput((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    const handleChangeSelect = (option: Option) => {
        const { name, id, field, url } = option;

        if (!field) return;
        if (field === 'fillerType' || field === 'fabricType') {
            clearValidation(['fillerType', 'fabricType']);
        } else {
            clearValidation(field);
        }

        setInput((prevState) => ({
            ...prevState,
            [field]: {
                name,
                id,
                url,
            },
        }));
    };

    const submit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const isFormValid = validate();

        if (!isFormValid) return;

        callback();
        // resetForm();
    };

    const validate = () => {
        let isValid = true;

        Object.entries(input).forEach(([key, value]) => {
            const inputErrorObj = validator(key, value as V, input);
            if (inputErrorObj) {
                isValid = false;
                setErrors((prev) => ({ ...prev, ...inputErrorObj }));
            }
        });

        return isValid;
    };

    const clearValidation = (fields?: Array<keyof Error> | keyof Error) => {
        if (Array.isArray(fields)) {
            setErrors((prevState) =>
                fields.reduce(
                    (acc, field) => {
                        acc[field] = undefined;
                        return acc;
                    },
                    { ...prevState }
                )
            );
            return;
        }

        if (fields) {
            setErrors((prevState) => ({
                ...prevState,
                [fields]: undefined,
            }));
            return;
        }

        setErrors({});
    };

    const resetForm = () => {
        setInput(initInput);
        setErrors({});
    };

    return { input, errors, setInput, handleChange, resetForm, submit, handleChangeSelect };
};

export default useForm;
